import srraf from "srraf"
import Debounce from "@/utils/debounce.js"
import getMediaQuery from "../lib/media-queries.js"
import EVENTS from "../lib/events.js"
const debounce = Debounce()

class NavigationWrapper extends HTMLElement {
  selectors = {
    // Shared
    nav: ".navigation",
    submenu: ".submenu",
    submenuContainer: ".submenu__container",
    submenuList: ".submenu__list",
    submenuItem: ".submenu__item",
    stateClasses: ".is-open, .is-opening, .is-closing, .active",
    submenuInner: ".submenu__inner",

    // Drawer
    drawerToggle: "label[for=navigation-control]",
    drawerToggleInput: ".navigation__control-input",
    drawerCloseLabel: ".navigation__control-close",
    secondaryDrawerToggle: ".submenu__list-control",
    secondaryDrawerToggleHeader: ".submenu-header__control",
    toggles: ".submenu__list-control-input",
    backdrop: ".navigation__backdrop",
    primary: ".submenu--primary",
    primaryInner: ".submenu__inner--primary",
    childAnimationTarget: ".submenu__header .submenu-header__control--back",

    // Dropdown
    secondaryDropdown: ".submenu--secondary",
    tertiaryDropdown: ".submenu--tertiary",
    primaryDropdownItem: ".submenu__item--primary-submenu",
    secondaryDropdownItem: ".submenu__item--secondary-submenu",

    // Megamenu
    megamenu: ".submenu--mega",
    promoImage: "[js-manual-load-animation]",
    themeEditorSelected: ".is-theme-editor-selected",

    // Crossborder
    language: "[js-language-drawer-trigger]",
    country: "[js-country-drawer-trigger]",

    scrollSentinels: "[data-scroll-start], [data-scroll-end]",
  }

  classes = {
    drawer: "navigation--drawer",
    drawerBreakpoint: "drawer-breakpoint",
    dropdownBreakpoint: "dropdown-breakpoint",

    openDrawer: "trigger-open-drawer-animation",
    closeDrawer: "trigger-close-drawer-animation",
    openDrawerPanel: "trigger-open-drawer-panel-animation",
    closeDrawerPanel: "trigger-close-drawer-panel-animation",

    openDropdown: "trigger-open-dropdown-animation",
    closeDropdown: "trigger-close-dropdown-animation",

    dropdownOpensLeft: "tertiary-menu--opens-left",

    // state classes
    open: "is-open",
    opening: "is-opening",
    closing: "is-closing",
    active: "active",
  }

  handleThemeEditorBlockEvent(evt) {
    const menu = this.nav.querySelector(
      `[data-block-id="${evt.detail.blockId}"]`,
    )
    if (!menu) return

    this.#closeOpenMenus()

    if (evt.type === EVENTS.SHOPIFY_BLOCK_SELECT) {
      menu.classList.add("is-open", "is-theme-editor-selected")
      menu.closest("li").classList.add("active")
      menu.querySelectorAll(this.selectors.promoImage).forEach((el) => {
        el.classList.add("visible")
      })
    } else {
      menu.classList.remove("is-open", "is-theme-editor-selected")
      menu.closest("li").classList.remove("active")
      menu.querySelectorAll(this.selectors.promoImage).forEach((el) => {
        el.classList.remove("visible")
      })
    }
  }

  connectedCallback() {
    this.eventController = new AbortController()
    const eventOptions = { signal: this.eventController.signal }

    document.addEventListener(
      EVENTS.SHOPIFY_SECTION_LOAD,
      (event) => {
        if (event?.detail?.sectionId === sectionId) {
          this.disconnectedCallback()
          this.connectedCallback()
        }
      },
      eventOptions,
    )

    if (window.Shopify.designMode) {
      ;[EVENTS.SHOPIFY_BLOCK_SELECT, EVENTS.SHOPIFY_BLOCK_DESELECT].forEach(
        (event) => {
          this.addEventListener(
            event,
            this.handleThemeEditorBlockEvent,
            eventOptions,
          )
        },
      )
    }

    this.nav = document.querySelector(this.selectors.nav)
    const nav = this.nav
    const sectionId = nav
      .closest(".shopify-section")
      .getAttribute("id")
      .replace("shopify-section-", "")

    // Megamenu promotion animate in offset
    this.promoVisibleTimingOffset = getComputedStyle(
      document.documentElement,
    ).getPropertyValue("--animation-timing-300")

    // warning! this is a hack to ensure
    // the menus have been fully painted before they are measured.
    setTimeout(() => {
      // Setup initial state
      this.#determineLayoutBreakpoint()
      this.#setLayoutClasses()
      this.#setEachSubmenuOpenHeight()
      this.#setMegamenuPosition()
      this.#setTertiaryDropdownOpeningDirection()
    }, 100)

    this.widthWatcher = srraf(({ vw, pvw, vh, pvh }) => {
      if (vw !== pvw || vh !== pvh) {
        debounce(() => {
          this.#closeOpenMenus()
          this.#determineLayoutBreakpoint()
          this.#setLayoutClasses()
          this.#setEachSubmenuOpenHeight()
          this.#setMegamenuPosition()
          this.#setTertiaryDropdownOpeningDirection()
        }, 100)
      }
    })

    const languageTrigger = nav.querySelector(this.selectors.language)
    if (languageTrigger) {
      languageTrigger.addEventListener(
        "click",
        () => {
          window.dispatchEvent(
            new CustomEvent(
              EVENTS.MODAL_MOBILE_CROSSBORDER_LANGUAGE_DRAWER_OPEN,
            ),
          )
        },
        eventOptions,
      )
    }

    const countryTrigger = nav.querySelector(this.selectors.country)
    if (countryTrigger) {
      countryTrigger.addEventListener(
        "click",
        () => {
          window.dispatchEvent(
            new CustomEvent(
              EVENTS.MODAL_MOBILE_CROSSBORDER_COUNTRY_DRAWER_OPEN,
            ),
          )
        },
        eventOptions,
      )
    }

    // Tap/Click toggles for drawer
    const drawerToggles = nav.querySelectorAll(
      `${this.selectors.drawerToggle}, ${this.selectors.backdrop}`,
    )
    drawerToggles.forEach((el) => {
      el.addEventListener(
        "click",
        this.handleDrawerToggleClick.bind(this),
        eventOptions,
      )
    })

    this.drawerCloseLabel = nav.querySelector(this.selectors.drawerCloseLabel)
    this.drawerCloseLabel.addEventListener(
      "keydown",
      this.handleDrawerCloseKeydown.bind(this),
      eventOptions,
    )

    this.drawerToggleInput = nav.querySelector(this.selectors.drawerToggleInput)
    this.drawerToggleInput.addEventListener(
      "keydown",
      this.handleDrawerToggleInputKeydown.bind(this),
      eventOptions,
    )

    const secondaryDrawerToggles = nav.querySelectorAll(`
      ${this.selectors.secondaryDrawerToggle},
      ${this.selectors.secondaryDrawerToggleHeader}
    `)
    secondaryDrawerToggles.forEach((toggle) => {
      toggle.addEventListener(
        "click",
        this.handleSecondaryDrawerToggleClick.bind(this),
        eventOptions,
      )
    })

    // Hover Dropdowns for primary submenus
    const dropdownTargets = nav.querySelectorAll(`
      ${this.selectors.primaryDropdownItem},
      ${this.selectors.secondaryDropdownItem}
    `)
    dropdownTargets.forEach((target) => {
      target.addEventListener(
        "mouseenter",
        this.handleDropdownMouseEvent.bind(this, "mouseenter"),
        eventOptions,
      )

      target.addEventListener(
        "mouseleave",
        this.handleDropdownMouseEvent.bind(this, "mouseleave"),
        eventOptions,
      )

      target.addEventListener(
        "touchstart",
        this.handleDropdownTouchEvent.bind(this),
        eventOptions,
      )
    })

    // scroll sentinels
    const submenuInners = nav.querySelectorAll(this.selectors.submenuInner)
    for (const root of submenuInners) {
      const scrollSentinels = root.querySelectorAll(
        this.selectors.scrollSentinels,
      )

      const observer = new IntersectionObserver(this.handleIntersectionChange, {
        root,
      })

      scrollSentinels.forEach((entry) => observer.observe(entry))
    }
  }

  disconnectedCallback() {
    this.eventController.abort()
    this.widthWatcher?.destroy()
  }

  handleIntersectionChange(entries) {
    entries.forEach((entry) => {
      entry.target.setAttribute(
        "data-visible",
        entry.isIntersecting ||
          (entry.target.hasAttribute("data-scroll-start") &&
            entry.target.parentNode.scrollTop === 0),
      )
    })
  }

  // Event Handlers
  // -------------------------------------

  handleDrawerToggleClick(event) {
    // We need the input to be "enabled", so that it can be focused (ie. reached
    // via tabbing). Because of this, we need to prevent the click from checking
    // the checkbox because we handle the associated functionality here in JS.
    event?.preventDefault()

    const { open, opening } = this.classes
    const target = this.nav.querySelector(this.selectors.primary)

    if (target.classList.contains(open) || target.classList.contains(opening)) {
      this.drawerClosePrimary()
    } else {
      this.drawerOpenPrimary()
    }
  }

  // Because we need the drawer toggle input to be enabled, we also need to prevent
  // the default keyboard interaction (pressing "space" interacts with a checkbox), and
  // allow for hitting "Enter" instead.
  handleDrawerToggleInputKeydown(event) {
    if (event.key === " ") {
      event.preventDefault()
    } else if (event.key === "Enter") {
      this.handleDrawerToggleClick()
    }
  }

  handleDrawerCloseKeydown(event) {
    if (event.key === "Enter") {
      this.drawerClosePrimary()
    }
  }

  handleSecondaryDrawerToggleClick(event) {
    const { open } = this.classes
    let target

    // Could be open/back or close button
    const submenuItem = event.target.closest(this.selectors.submenuItem)
    if (!submenuItem) {
      target = event.target.closest(this.selectors.submenu)
    } else {
      target = submenuItem.querySelector(this.selectors.submenu)
    }

    if (target.classList.contains(open)) {
      this.drawerCloseSecondary(target)
    } else {
      this.drawerOpenSecondary(target)
    }
  }

  handleDropdownMouseEvent(eventDirection, event) {
    if (
      window.Shopify.designMode &&
      event.target.querySelector(this.selectors.themeEditorSelected)
    ) {
      return
    }

    if (this.isDrawer) return
    if (event.target.closest(this.selectors.megamenu)) return

    if (eventDirection === "mouseleave") {
      this.closeDropdown(event.target)
    } else {
      this.openDropdown(event.target)
    }
  }

  handleDropdownTouchEvent(event) {
    if (this.isDrawer) return

    if (event.target.classList.contains("submenu__item--parent")) {
      // Chrome: touchstart clicks the nearest anchor
      event.preventDefault()

      this.handleDropdownMouseEvent("mouseenter", event)
    } else {
      let target = event.target

      // a parent item's span will be the target on the second touch
      if (target.tagName === "SPAN") {
        target = target.closest(".submenu__item")
      }

      target.querySelector("a").click()
    }
  }

  // Drawer
  // -------------------------------------

  drawerOpenPrimary() {
    const { openDrawer, opening, open, closing } = this.classes
    const targetElements = [
      this.nav.querySelector(this.selectors.primary),
      this.nav.querySelector(this.selectors.backdrop),
    ]

    targetElements.forEach((target) => {
      target.classList.add(openDrawer, opening)
    })

    const animatingElements = targetElements.flatMap((el) =>
      el.getAnimations({ subtree: true }),
    )
    const pendingAnimationEnds = animatingElements.map(
      (animation) => animation.finished,
    )

    Promise.all(pendingAnimationEnds)
      .then(() => {
        targetElements.forEach((target) => {
          target.classList.remove(openDrawer, opening)
          target.classList.add(open)
        })

        this.drawerCloseLabel.focus()
      })
      .catch(() => {
        // Manually cleanup state and classes if an animation is interrupted
        // by closing before open
        targetElements.forEach((target) => {
          target.classList.remove(openDrawer, opening)
          target.classList.remove(openDrawer, open)
          target.classList.add(closing)
        })
      })
  }

  drawerClosePrimary() {
    const { closeDrawer, closing, open } = this.classes
    const targetElements = [
      this.nav.querySelector(this.selectors.primary),
      this.nav.querySelector(this.selectors.backdrop),
    ]

    targetElements.forEach((target) => {
      target.classList.add(closeDrawer, closing)
    })

    const animatingElements = targetElements.flatMap((el) =>
      el.getAnimations({ subtree: true }),
    )
    const pendingAnimationEnds = animatingElements.map(
      (animation) => animation.finished,
    )

    Promise.all(pendingAnimationEnds).then(() => {
      targetElements.forEach((target) => {
        target.classList.remove(closeDrawer, closing, open)
      })

      this.drawerToggleInput.focus()
    })
  }

  drawerOpenSecondary(target) {
    const { openDrawerPanel, opening, open } = this.classes

    target.classList.add(openDrawerPanel, opening)

    const animatingElements = target.getAnimations({ subtree: true })
    const pendingAnimationEnds = animatingElements.map(
      (animation) => animation.finished,
    )

    const promos = target.querySelectorAll(this.selectors.promoImage)
    promos.forEach((el) => el.classList.add("visible"))

    Promise.all(pendingAnimationEnds).then(() => {
      target.classList.remove(openDrawerPanel, opening)
      target.classList.add(open)
    })
  }

  drawerCloseSecondary(target) {
    const {
      active,
      openDrawerPanel,
      closeDrawerPanel,
      closing,
      open,
      opening,
    } = this.classes

    target.classList.add(closeDrawerPanel, closing)

    const animatingElements = target.getAnimations({ subtree: true })
    const pendingAnimationEnds = animatingElements.map(
      (animation) => animation.finished,
    )
    Promise.all(pendingAnimationEnds)
      .then(() => {
        target.classList.remove(closeDrawerPanel, closing, open, active)
        this.#closeOpenMenus()
      })
      .catch(() => {
        // Manually cleanup state and classes if an animation is interrupted
        this.nav.querySelectorAll(this.selectors.stateClasses).forEach((el) => {
          el.classList.remove(
            open,
            opening,
            closing,
            openDrawerPanel,
            closeDrawerPanel,
            active,
          )
        })
      })
  }

  // Dropdown
  // -------------------------------------

  openDropdown(target) {
    const { closeDropdown, openDropdown, closing, opening, open } = this.classes
    const submenuTarget = target.querySelector(this.selectors.submenu)

    // remove opening animation if retriggering
    submenuTarget.classList.remove(closeDropdown, closing)
    submenuTarget.classList.add(openDropdown, opening)

    const animatingElements = submenuTarget.getAnimations({ subtree: true })
    const pendingAnimationEnds = animatingElements.map(
      (animation) => animation.finished,
    )

    const promos = target.querySelectorAll(this.selectors.promoImage)
    // Delay the promo image animation to allow the dropdown to open a bit before
    // starting the loaded/visible animation.
    setTimeout(() => {
      promos.forEach((el) => el.classList.add("visible"))
    }, this.promoVisibleTimingOffset)

    Promise.all(pendingAnimationEnds)
      .then(() => {
        submenuTarget.classList.remove(openDropdown, opening)
        submenuTarget.classList.add(open)
      })
      .catch(() => {
        // Manually cleanup attributes and classes if an animation is interrupted
        submenuTarget.classList.remove(openDropdown, opening, open)
      })
  }

  closeDropdown(target) {
    const { openDropdown, closeDropdown, opening, open, closing } = this.classes
    const submenuTarget = target.querySelector(this.selectors.submenu)

    // remove opening animation if retriggering
    submenuTarget.classList.remove(openDropdown, opening, open)
    submenuTarget.classList.add(closeDropdown, closing)

    const animatingElements = submenuTarget.getAnimations({ subtree: true })
    const pendingAnimationEnds = animatingElements.map(
      (animation) => animation.finished,
    )
    Promise.all(pendingAnimationEnds)
      .then(() => {
        submenuTarget.classList.remove(closeDropdown, closing, open)
      })
      .catch(() => {
        // Manually cleanup attributes and classes if an animation is interrupted
        submenuTarget.classList.remove(closeDropdown, closing, open)
      })
  }

  // Private
  // -------------------------------------

  #determineLayoutBreakpoint() {
    // regex matches a floating number from the media query string
    const breakpointRegex = /([0-9.]+)/g

    const breakpoint = parseFloat(
      getMediaQuery("small-desktop").match(breakpointRegex)[0],
    )

    return (this.breakpoint = breakpoint)
  }

  #setEachSubmenuOpenHeight() {
    const submenus = this.nav.querySelectorAll(this.selectors.submenu)
    submenus.forEach((el) => this.#setOpenHeightProp(el))
  }

  #setLayoutClasses() {
    const nav = this.nav

    nav.querySelectorAll(this.selectors.stateClasses).forEach((el) => {
      el.classList.remove(
        this.classes.open,
        this.classes.opening,
        this.classes.closing,
      )
    })

    if (nav.classList.contains(this.classes.drawer)) {
      this.isDrawer = true
      return nav.classList.add(this.classes.drawerBreakpoint)
    }

    const isDrawer = (this.isDrawer = window.innerWidth < this.breakpoint)

    nav.classList.toggle(this.classes.drawerBreakpoint, isDrawer)
    nav.classList.toggle(this.classes.dropdownBreakpoint, !isDrawer)
  }

  #setOpenHeightProp(el) {
    const inner = el.querySelector(this.selectors.submenuInner)
    if (!inner) return

    el.style.removeProperty("--js-max-height")
    inner.style.maxHeight = ""

    // only set the height if the window is large enough for dropdowns
    if (window.innerWidth < this.breakpoint || this.isDrawer) return

    const { top } = el.getBoundingClientRect()
    const screenHeightLimit = window.innerHeight - top - 35

    const menuHeight = el.querySelector(
      this.selectors.submenuContainer,
    )?.offsetHeight
    if (!menuHeight) return

    const maxHeight = Math.min(screenHeightLimit, menuHeight)

    el.style.setProperty("--js-max-height", `${maxHeight}px`)
    inner.style.maxHeight = `${maxHeight}px`
  }

  #setMegamenuPosition() {
    // mega menus are positioned to the left edge of the screen
    // this needs to be calculated as an offset from the left edge of the nav
    // when the window is resized, the calculation is run again, but
    // the existing offset will be taken into account by `getBoundingClientRect`
    // this needs to be compensated for by storing it on the element.

    if (!this.megamenus) {
      this.megamenus = this.nav.querySelectorAll(this.selectors.megamenu)
    }

    this.megamenus.forEach((nav) => {
      nav.style.removeProperty("--js-left", "0px")
      nav.removeAttribute("data-left")

      const currentLeft = Number(nav.getAttribute("data-left")) || 0
      // Left from client doesn't account for horizontal scrolling which is needed
      // to prevent the nav going wider than the screen
      const scrollAccountedLeft =
        nav.getBoundingClientRect().left + window.scrollX
      const newLeft = scrollAccountedLeft + currentLeft

      nav.style.setProperty("--js-left", `${Math.abs(newLeft)}px`)
      nav.setAttribute("data-left", newLeft)
    })
  }

  #setTertiaryDropdownOpeningDirection() {
    // Default is to open the right if space permits,
    // otherwise it opens to the left to prevent expanding the site width
    const dropdowns = this.querySelectorAll(this.selectors.secondaryDropdown)
    const submenuWidth = parseInt(
      getComputedStyle(document.documentElement)
        .getPropertyValue("--submenu-width")
        .replace("px", ""),
      10,
    )

    dropdowns.forEach((dropdown) => {
      // The secondary menu never moves making it more stable for screen size changes
      const tertiaryRightPlacement =
        dropdown.getBoundingClientRect().right + submenuWidth
      const tertiaryMenuTooFarRight = tertiaryRightPlacement > window.innerWidth
      const tertiaries = dropdown.querySelectorAll(
        this.selectors.tertiaryDropdown,
      )

      tertiaries.forEach((tertiary) => {
        tertiary.classList.toggle(
          this.classes.dropdownOpensLeft,
          tertiaryMenuTooFarRight,
        )
      })
    })
  }

  #closeOpenMenus(parent = this.nav) {
    document.querySelectorAll(this.selectors.toggles).forEach((el) => {
      el.checked = false
    })
  }
}

if (!customElements.get("navigation-wrapper")) {
  customElements.define("navigation-wrapper", NavigationWrapper)
}
